import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { selectAdminToken } from './sessionSlice';
import { apiUrl } from '../utils/apiUrl';

export const fetchThemeData = createAsyncThunk(
  'api/fetchThemeData',
  async (
    { themeId = null, isAdmin = false },
    { getState, rejectWithValue }
  ) => {
    try {
      const state = getState();
      const adminToken = selectAdminToken(state);

      console.log("hitting fetchThemeData");
      const response = await fetch(
        apiUrl('/fetchThemeData'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            currentTheme: themeId,
          }),
        }
      );


      if (!response.ok) throw new Error('Network response was not ok');
      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Attempt to load a persisted themeName from localStorage
const persistedTheme = localStorage.getItem('currentTheme');

const themeSlice = createSlice({
  name: 'theme',
  initialState: {
    currentTheme: persistedTheme || null,
    availableThemes: [],
    status: 'idle',
    error: null,
    data: null,
    // Tracks concurrent fetchThemeData calls so a losing race (common on
    // brand-new sessions while themes are seeded) does not flash "failed".
    pendingCount: 0,
  },
  reducers: {
    // Direct theme selection: update both state and localStorage
    selectTheme: (state, action) => {
      state.currentTheme = action.payload;
      localStorage.setItem('currentTheme', action.payload);
    },
    // Clear theme: remove from state and localStorage
    resetTheme: (state) => {
      state.currentTheme = null;
      localStorage.removeItem('currentTheme');
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchThemeData.pending, (state) => {
        state.pendingCount += 1;
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchThemeData.fulfilled, (state, action) => {
        state.pendingCount = Math.max(0, state.pendingCount - 1);
        state.status = state.pendingCount > 0 ? 'loading' : 'succeeded';
        state.error = null;
        // Save payload as-is if you need it
        state.data = action.payload?.data ?? null;

        // Extract just the theme name from payload.data
        const themeName = action.payload?.data?.themename;
        if (themeName) {
          state.currentTheme = themeName;
          localStorage.setItem('currentTheme', themeName);
        }

        // If admin mode, store available themes
        if (action.meta.arg.isAdmin) {
          state.availableThemes = action.payload.availableThemes || [];
        }
      })
      .addCase(fetchThemeData.rejected, (state, action) => {
        state.pendingCount = Math.max(0, state.pendingCount - 1);
        // Another in-flight request may still succeed (new-session seed race).
        if (state.pendingCount > 0) {
          state.status = 'loading';
          return;
        }
        // Keep last good data instead of flashing a hard error.
        if (state.data) {
          state.status = 'succeeded';
          state.error = action.payload;
          return;
        }
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export const { selectTheme, resetTheme } = themeSlice.actions;
export default themeSlice.reducer;
